import React from 'react';
import { Formik, Form, FormikValues } from 'formik';
import { FocActionRow } from '~/components/Patient';
import { usePatientService } from '~/services';
import {
    sortFocActions,
    focGroupingValidation,
    buildFocSubmissionAnswers,
    gapIsPending,
} from '~/util';

export type FocInputValueType = null | boolean | string | number | Date;

export interface FocGapFormProps {
    focGap: GapType;
    formId: string;
    patientId: number;
    onSubmitSuccess: () => void;
    setFormDirty: (x: boolean) => void;
    setSaving: (x: boolean) => void;
    setDisabled: (x: boolean) => void;
}

export function FocGapForm({
    focGap,
    formId,
    patientId,
    onSubmitSuccess,
    setFormDirty,
    setDisabled,
    setSaving,
}: FocGapFormProps) {
    const { updateFocGap } = usePatientService();
    const [formValues, setFormValues] = React.useState<Record<string, FocInputValueType>>({});
    const dirtyFlag = React.useRef(false);
    const submitted = React.useRef(false);

    function handleFormInputChange(id: string | number, value: FocInputValueType) {
        setFormValues((prev) => ({
            ...prev,
            [id]: value,
        }));
    }

    async function handleSave(values: FormikValues) {
        const responses = buildFocSubmissionAnswers(values);
        if (responses.length === 0) return;
        try {
            setSaving(true);
            setDisabled(true);
            await updateFocGap(
                focGap.id,
                patientId,
                {
                    submission_answer: { metadata: { responses } },
                    updated_at: focGap.updated_at,
                },
                // Explicitly pop-up ApiErrorResponse modal to handle backend validation errors
                { handledErrors: { 400: false } }
            );
            onSubmitSuccess();
        } catch (error) {
            setSaving(false);
            setDisabled(false);
        }
    }

    return (
        <Formik
            enableReinitialize={!submitted.current}
            initialValues={formValues}
            validateOnChange
            validateOnBlur={submitted.current}
            onSubmit={handleSave}
            validate={(values) => focGroupingValidation(values, focGap.measure)}
        >
            {(formik) => {
                if (!submitted.current && formik.isSubmitting) submitted.current = true;
                if (dirtyFlag.current !== formik.dirty) {
                    dirtyFlag.current = formik.dirty;
                    // timeout allows the state update to run on the next tick after render
                    setTimeout(() => {
                        setFormDirty(formik.dirty);
                    }, 0);
                }
                return (
                    <Form id={formId}>
                        <table className="gap-table foc-gap-table align-middle">
                            <thead>
                                <tr>
                                    <th className="checkbox-col">
                                        <span className="sr-only">Checkbox column</span>
                                    </th>
                                    <th className="col-gap-action-name">Measure</th>
                                    <th className="w-20">Acceptable Dates</th>
                                    <th className="w-10">Frequency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {focGap.measure?.focs?.map((foc) =>
                                    foc.foc_actions
                                        ?.sort(sortFocActions)
                                        .map((fga) => (
                                            <FocActionRow
                                                key={fga.id}
                                                action={fga}
                                                submissionAnswer={focGap.submission_answer}
                                                handleChange={handleFormInputChange}
                                                isSingle={
                                                    foc.foc_actions?.length === 1 &&
                                                    focGap.measure?.focs?.length === 1
                                                }
                                                readOnly={!gapIsPending(focGap)}
                                                formik={formik}
                                            />
                                        ))
                                )}
                            </tbody>
                        </table>
                    </Form>
                );
            }}
        </Formik>
    );
}
